import React, { useEffect, useState } from "react";
import { apiCall } from "utils/httpClient";
import { successToast, errorToast } from "utils/httpClient";
import apiEndPoints from "utils/apiEndPoints";
import { handleDetailResponse } from "utils/utils";
import strings from "translate/language";
import "./VisitorCmAllocation.css";

// JUSTO-64: GRE allocation modal – allocate visitor to Closing Manager or SCM (dual role). List = CMs + SCM only; no SM list.
const AllocateSM = ({
  show,
  propertyId,
  leadId,
  visitorName = "",
  isReallocate = false,
  currentCmId = null,
  currentCmName = "",
  handleClose,
  onAllocateSuccess,
}) => {
  const [managerList, setManagerList] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [fetchingList, setFetchingList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const currentCmIdStr = currentCmId ? String(currentCmId) : "";

  const filteredList = managerList
    .filter(
      (item) =>
        item?.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item?.mobile?.includes(searchQuery),
    )
    .filter(
      (item) =>
        !isReallocate ||
        !currentCmIdStr ||
        String(item?._id || item?.user_id || "") !== currentCmIdStr,
    );

  useEffect(() => {
    if (!show) {
      setSelectedPerson(null);
      setSearchQuery("");
      setManagerList([]);
      return;
    }

    const fetchCMs = async () => {
      if (!propertyId) return;

      setFetchingList(true);
      try {
        const cmResponse = await apiCall(
          "post",
          apiEndPoints.GET_PROPERTY_BASED_CM,
          { property_id: propertyId },
        );

        const cmData =
          cmResponse?.data?.status === 200 ? cmResponse.data.data || [] : [];
        setManagerList(cmData);

        if (
          cmResponse?.data?.status !== 200 &&
          cmResponse?.data?.status !== 201
        ) {
          errorToast(
            cmResponse?.data?.message || "Failed to fetch Closing Managers",
          );
        }
      } catch (error) {
        console.error("Error fetching Closing Managers list:", error);
        errorToast("Failed to fetch Closing Managers");
        setManagerList([]);
      } finally {
        setFetchingList(false);
      }
    };

    fetchCMs();
  }, [show, propertyId]);

  const handleSelection = (item) => {
    setSelectedPerson(item);
  };

  const getRoleLabel = (item) => (item?.role === "SCM" ? "SCM" : "CM");

  const renderRoleBadge = (role) => {
    const isScm = role === "SCM";
    return (
      <span
        className={`cm-alloc-modal__role ${isScm ? "cm-alloc-modal__role--scm" : "cm-alloc-modal__role--cm"}`}
      >
        {isScm ? "SCM" : "CM"}
      </span>
    );
  };

  const handleAllocate = async () => {
    if (!selectedPerson) {
      errorToast("Please select a Closing Manager");
      return;
    }

    if (!leadId) {
      errorToast("Lead ID is missing");
      return;
    }

    if (
      isReallocate &&
      currentCmIdStr &&
      String(selectedPerson._id || selectedPerson.user_id || "") ===
        currentCmIdStr
    ) {
      errorToast("Please select a different Closing Manager");
      return;
    }

    setSubmitting(true);
    try {
      const visitorDetailResponse = await apiCall(
        "post",
        apiEndPoints.VISITORDETAIL,
        { lead_id: leadId },
      );

      if (visitorDetailResponse?.data?.status !== 200) {
        errorToast("Failed to fetch visitor details");
        return;
      }

      const visitorData = handleDetailResponse(
        visitorDetailResponse?.data?.data || {},
      );

      const customerData = visitorData?.customer_detail || {};
      const first_name =
        customerData?.first_name || visitorData?.first_name || "";
      const mobile = customerData?.mobile || visitorData?.mobile || "";
      const property_id = visitorData?.property_id || propertyId || "";

      if (!first_name || !mobile || !leadId) {
        errorToast("Visitor data is incomplete. Please try again.");
        return;
      }

      if (!visitorData || !visitorData._id) {
        errorToast("Invalid visitor data. Please try again.");
        return;
      }

      if (!visitorData.create_by) {
        errorToast("Lead data is incomplete. Cannot allocate.");
        return;
      }

      const payload = {
        lead_id: leadId,
        for_cm_id: selectedPerson._id,
        first_name,
        mobile,
      };

      if (property_id) {
        payload.property_id = property_id;
      }

      const response = await apiCall(
        "post",
        apiEndPoints.EDITVISITOR,
        payload,
      );

      if (response?.data?.status === 200) {
        successToast(
          response?.data?.message ||
            (isReallocate
              ? "Visitor re-allocated successfully"
              : "Visitor allocated successfully"),
        );
        if (onAllocateSuccess) {
          onAllocateSuccess(selectedPerson);
        }
        handleClose();
      } else {
        errorToast(response?.data?.message || "Failed to allocate visitor");
      }
    } catch (error) {
      console.error("Error allocating visitor:", error);
      errorToast("Failed to allocate visitor. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  const isBusy = fetchingList || submitting;

  return (
    <>
      <div
        className="modal-backdrop show"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0, 0, 0, 0.4)",
          zIndex: 1040,
        }}
        onClick={!isBusy ? handleClose : undefined}
      />

      <div
        className="modal show d-block"
        style={{ zIndex: 1050 }}
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-md cm-alloc-modal__dialog">
          <div className="modal-content border-0 shadow">
            <div className="modal-header cm-alloc-modal__header border-0">
              <div>
                <h5 className="modal-title font-weight-bold mb-1">
                  {isReallocate
                    ? strings.reassignVisitorToCM || strings.reAllocateVisitorToCM
                    : strings.allocateVisitorToCM ||
                      "Allocate Visitor to Closing Manager"}
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
                  {strings.selectNewClosingManager}
                </p>
              </div>
              <button
                type="button"
                className="close"
                onClick={handleClose}
                disabled={submitting}
                aria-label="Close"
              >
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body cm-alloc-modal__body">
              <div className="cm-alloc-modal__context">
                {visitorName ? (
                  <div className="cm-alloc-modal__context-row">
                    <span className="cm-alloc-modal__context-label">Visitor</span>
                    <span
                      className="cm-alloc-modal__context-value"
                      title={visitorName}
                    >
                      {visitorName}
                    </span>
                  </div>
                ) : null}
                {isReallocate && currentCmName ? (
                  <div className="cm-alloc-modal__context-row">
                    <span className="cm-alloc-modal__context-label">
                      {strings.currentlyAllocatedTo || "Currently allocated to"}
                    </span>
                    <span
                      className="cm-alloc-modal__context-value"
                      title={currentCmName}
                    >
                      {currentCmName}
                    </span>
                  </div>
                ) : null}
              </div>

              {selectedPerson ? (
                <div className="cm-alloc-modal__selection">
                  <div
                    className="d-flex align-items-center min-w-0 flex-grow-1"
                    style={{ gap: 8 }}
                  >
                    <span className="text-muted flex-shrink-0">
                      {isReallocate ? "Reassign to" : "Selected"}:
                    </span>
                    <strong
                      className="text-truncate"
                      style={{ color: "#1f2937", fontWeight: 600 }}
                    >
                      {selectedPerson.user_name}
                    </strong>
                    {renderRoleBadge(getRoleLabel(selectedPerson))}
                  </div>
                  <button
                    type="button"
                    className="btn btn-link btn-sm text-muted p-0 flex-shrink-0"
                    onClick={() => setSelectedPerson(null)}
                    disabled={submitting}
                  >
                    {strings.cmClearSelection || "Clear"}
                  </button>
                </div>
              ) : null}

              <span className="cm-alloc-modal__section-label">
                {isReallocate
                  ? strings.selectNewClosingManager
                  : "Select Closing Manager / SCM"}
              </span>

              <div className="cm-alloc-modal__search">
                <i
                  className="fa fa-search cm-alloc-modal__search-icon"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  className="form-control cm-alloc-modal__search-input"
                  placeholder="Search by name, email or mobile"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={fetchingList || submitting}
                />
              </div>

              <div className="cm-alloc-modal__list">
                {fetchingList ? (
                  <div className="cm-alloc-modal__empty">
                    <i className="fa fa-spinner fa-spin mr-2" />
                    Loading Closing Managers...
                  </div>
                ) : managerList.length === 0 ? (
                  <div className="cm-alloc-modal__empty">
                    No Closing Managers found for this property.
                  </div>
                ) : filteredList.length === 0 ? (
                  <div className="cm-alloc-modal__empty">
                    No one matches your search.
                  </div>
                ) : (
                  filteredList.map((item, index) => {
                    const isSelected = selectedPerson?._id === item._id;
                    const roleLabel = getRoleLabel(item);

                    return (
                      <label
                        key={`${item._id}-${item.role || "cm"}-${index}`}
                        className={`cm-alloc-modal__list-item w-100${isSelected ? " cm-alloc-modal__list-item--selected" : ""}`}
                        style={{
                          cursor: submitting ? "not-allowed" : "pointer",
                        }}
                      >
                        <input
                          type="radio"
                          name="selectedPerson"
                          className="mr-3 flex-shrink-0"
                          checked={isSelected}
                          onChange={() => handleSelection(item)}
                          disabled={submitting}
                        />
                        <div className="flex-grow-1 min-w-0">
                          <div
                            className="d-flex align-items-center justify-content-between"
                            style={{ gap: 10 }}
                          >
                            <span
                              className="cm-alloc-modal__list-name text-truncate"
                              style={{
                                color: isSelected ? "#1e3a8a" : "#1e293b",
                              }}
                            >
                              {item.user_name}
                            </span>
                            {renderRoleBadge(roleLabel)}
                          </div>
                          {item.email || item.mobile ? (
                            <div className="cm-alloc-modal__list-meta text-truncate">
                              {[item.email, item.mobile]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          ) : null}
                        </div>
                        {isSelected ? (
                          <i
                            className="fa fa-check-circle ml-2 flex-shrink-0"
                            style={{ color: "#4466f2", fontSize: 18 }}
                            aria-hidden="true"
                          />
                        ) : null}
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            <div className="modal-footer cm-alloc-modal__footer border-0 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-light border"
                onClick={handleClose}
                disabled={submitting}
                style={{ minWidth: 88, borderRadius: 6 }}
              >
                {strings.close}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAllocate}
                disabled={submitting || !selectedPerson || fetchingList}
                style={{
                  minWidth: 108,
                  borderRadius: 6,
                  fontWeight: 600,
                }}
              >
                {submitting ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-1" />
                    {isReallocate ? "Saving..." : "Allocating..."}
                  </>
                ) : isReallocate ? (
                  strings.reassignToCM || "Reassign"
                ) : (
                  strings.allocate
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllocateSM;
